import { ApolloClient, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GET_EMAIL_INCOMINGS, GET_EMAIL_REQUESTS } from "graphql/query/communication-gql";
import CommunicationConst from "../communication-const";
import { getCommunicationDayKey } from "./communication-date-helper";
import type {
  TCommunicationAuditEntry,
  TCommunicationDismissReason,
  TCommunicationEquipmentChange,
  TCommunicationFilters,
  TCommunicationFinalChanges,
  TCommunicationQuestion,
  TCommunicationQueueTotals,
  TCommunicationRequestGroup,
  TCommunicationRequestItem,
  TCommunicationStandUpdates,
  TCommunicationSummary,
  TCommunicationTask,
  TCommunicationTaskCapabilities,
  TCommunicationTaskFormValues,
  TCommunicationTaskWorkState,
} from "./communication-types-helper";

type TCommunicationWhere = {
  column?: string;
  operator?: string;
  value?: unknown;
  AND?: TCommunicationWhere[];
  OR?: TCommunicationWhere[];
};

type TPaginatorData<TItem> = {
  paginatorInfo?: {
    lastPage?: number | null;
  };
  data?: TItem[];
};

type TRawEmailRequest = {
  id?: string | null;
  email_incoming_id?: string | null;
  ee_id?: string | null;
  stand_id?: string | null;
  equipment_id?: string | null;
  flooring_id?: string | null;
  exhibition_name?: string | null;
  company_name?: string | null;
  stand_number?: string | null;
  action?: "ADD" | "REMOVE" | "CHANGE" | "QUESTION" | null;
  item_name?: string | null;
  item_qty?: number | null;
  item_notes?: string | null;
  confidence?: number | null;
  status?: "PENDING" | "UNIDENTIFIED" | "APPROVED" | "REJECTED" | "CANCELLED" | null;
  ai_data?: unknown;
  processed_by_id?: string | null;
  processed_at?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  relExhibitionEvent?: {
    id?: string | null;
    title?: string | null;
    organizer_id?: string | null;
    exhibition_type_id?: string | null;
  } | null;
  relStand?: {
    id?: string | null;
    stand_number?: string | null;
    company_name?: string | null;
  } | null;
  relEquipment?: {
    id?: string | null;
    group_id?: string | null;
    title?: string | null;
    color?: string | null;
  } | null;
  relFlooring?: {
    id?: string | null;
    title?: string | null;
    color?: string | null;
  } | null;
};

type TRawEmailIncoming = {
  id?: string | null;
  subject?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  body_clean?: string | null;
  intent?: string | null;
  status?: string | null;
  parse_error?: string | null;
  parsed_data?: unknown;
  used_token?: number | null;
  received_at?: string | null;
  parsed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  relEmailChannel?: {
    id?: string | null;
    title?: string | null;
  } | null;
  relEmailRequests?: TRawEmailRequest[] | null;
};

const INCOMING_PAGE_SIZE = 50;
const REQUEST_PAGE_SIZE = 100;
const INCOMING_ID_CHUNK_SIZE = 50;
const REQUEST_FILTER_FIELDS: Array<keyof Pick<
  TCommunicationFilters,
  "event_name" | "company_name" | "stand_number"
>> = [ "event_name", "company_name", "stand_number" ];

const AI_SUMMARY_KEYS = [ "ai_summary", "summary", "short_summary", "overview" ];
const AI_REASON_KEYS = [ "ai_reason", "reason", "explanation", "match_reason" ];

const createFinalChanges = (
  equipmentChanges: TCommunicationEquipmentChange[],
  standUpdates: TCommunicationStandUpdates,
  managerNote: string,
  replyHandoffNote = ""
): TCommunicationFinalChanges => ({
  equipment_changes: equipmentChanges,
  stand_updates: standUpdates,
  manager_note: managerNote,
  reply_handoff_note: replyHandoffNote,
});

const mapFormValuesToFinalChanges = (
  values: TCommunicationTaskFormValues
): TCommunicationFinalChanges =>
  createFinalChanges(
    values.equipment_changes || [],
    values.stand_updates || {},
    values.manager_note || "",
    values.reply_handoff_note || ""
  );

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const pickFirstNonEmpty = (...values: unknown[]): string =>
  values
    .map((value) => getString(value))
    .find(Boolean) || "";

const normalizeConfidence = (value: number | null | undefined): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  return value > 0 && value <= 1 ? Math.round(value * 100) : Math.round(value);
};

const uniqueValues = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

const chunkArray = <T,>(items: T[], chunkSize: number): T[][] => {
  if (!items.length) {
    return [];
  }

  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }

  return chunks;
};

const nestConditions = (
  conditions: Array<TCommunicationWhere | undefined>,
  condition: "AND" | "OR"
): TCommunicationWhere | undefined => {
  const filteredConditions = conditions.filter(Boolean) as TCommunicationWhere[];

  if (!filteredConditions.length) {
    return undefined;
  }

  const [ currentCondition, ...rest ] = filteredConditions;

  if (!rest.length) {
    return currentCondition;
  }

  return {
    ...currentCondition,
    [ condition ]: [ nestConditions(rest, condition) as TCommunicationWhere ],
  };
};

const buildLikeValue = (value: string): string => `%${value.trim()}%`;

const buildRequestWhere = (filters: TCommunicationFilters): TCommunicationWhere | undefined =>
  nestConditions(
    [
      filters.event_name
        ? {
          column: "EXHIBITION_NAME",
          operator: "LIKE",
          value: buildLikeValue(filters.event_name),
        }
        : undefined,
      filters.company_name
        ? {
          column: "COMPANY_NAME",
          operator: "LIKE",
          value: buildLikeValue(filters.company_name),
        }
        : undefined,
      filters.stand_number
        ? {
          column: "STAND_NUMBER",
          operator: "LIKE",
          value: buildLikeValue(filters.stand_number),
        }
        : undefined,
    ],
    "AND"
  );

const buildIncomingWhere = (
  filters: TCommunicationFilters,
  incomingIds?: string[]
): TCommunicationWhere | undefined => {
  const conditions: Array<TCommunicationWhere | undefined> = [];

  if (incomingIds?.length) {
    conditions.push({
      column: "ID",
      operator: "IN",
      value: incomingIds,
    });
  }

  if (filters.sender.trim()) {
    conditions.push(
      nestConditions(
        [
          {
            column: "FROM_EMAIL",
            operator: "LIKE",
            value: buildLikeValue(filters.sender),
          },
          {
            column: "FROM_NAME",
            operator: "LIKE",
            value: buildLikeValue(filters.sender),
          },
        ],
        "OR"
      )
    );
  }

  if (filters.received_date) {
    conditions.push({
      column: "RECEIVED_AT",
      operator: "GTE",
      value: `${filters.received_date}T00:00:00`,
    });
    conditions.push({
      column: "RECEIVED_AT",
      operator: "LTE",
      value: `${filters.received_date}T23:59:59`,
    });
  }

  return nestConditions(conditions, "AND");
};

const hasRequestLevelFilters = (filters: TCommunicationFilters): boolean =>
  REQUEST_FILTER_FIELDS.some((field) => Boolean(filters[ field ]?.trim()));

const readNestedString = (value: unknown, keys: string[], depth = 0): string => {
  if (depth > 3 || value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedValue = readNestedString(item, keys, depth + 1);

      if (nestedValue) {
        return nestedValue;
      }
    }

    return "";
  }

  if (typeof value !== "object") {
    return "";
  }

  const record = value as Record<string, unknown>;

  for (const key of keys) {
    const directValue = record[ key ];

    if (typeof directValue === "string" && directValue.trim()) {
      return directValue.trim();
    }
  }

  for (const nestedValue of Object.values(record)) {
    const extractedValue = readNestedString(nestedValue, keys, depth + 1);

    if (extractedValue) {
      return extractedValue;
    }
  }

  return "";
};

const getLatestDate = (values: Array<string | null | undefined>): string | undefined => {
  const normalizedValues = uniqueValues(values.map((value) => getString(value)));

  if (!normalizedValues.length) {
    return undefined;
  }

  return normalizedValues.sort(
    (left, right) => new Date(right).getTime() - new Date(left).getTime()
  )[ 0 ];
};

const getNormalizedStandNumbers = (values: Array<string | null | undefined>): string[] =>
  uniqueValues(
    values
      .map((value) => getString(value))
      .filter(Boolean)
  );

const DEMO_MULTI_STAND_TASK_ID = "demo-multi-stand-email";

const includesNormalized = (source: string | null | undefined, search: string): boolean =>
  getString(source).toLowerCase().includes(search.toLowerCase().trim());

const matchesClientSideFilters = (
  task: TCommunicationTask,
  filters: TCommunicationFilters
): boolean => {
  const eventSearch = filters.event_name.trim().toLowerCase();
  const companySearch = filters.company_name.trim().toLowerCase();
  const standSearch = filters.stand_number.trim().toLowerCase();
  const senderSearch = filters.sender.trim().toLowerCase();

  if (
    senderSearch
    && ![
      task.sender,
      task.sender_email || "",
    ].some((value) => includesNormalized(value, senderSearch))
  ) {
    return false;
  }

  if (
    eventSearch
    && ![
      task.event_name,
      ...task.request_items.map((request) => request.event_name),
    ].some((value) => includesNormalized(value, eventSearch))
  ) {
    return false;
  }

  if (
    companySearch
    && ![
      task.company_name,
      ...task.request_items.map((request) => request.company_name),
    ].some((value) => includesNormalized(value, companySearch))
  ) {
    return false;
  }

  if (
    standSearch
    && ![
      task.stand_number,
      ...task.request_items.map((request) => request.stand_number),
    ].some((value) => includesNormalized(value, standSearch))
  ) {
    return false;
  }

  if (
    filters.received_date
    && getCommunicationDayKey(task.received_at) !== filters.received_date
  ) {
    return false;
  }

  return true;
};

const getTaskStatusFromValues = (
  values: Pick<TCommunicationTaskFormValues, "event_name" | "company_name" | "stand_number" | "stand_numbers">,
  isManagerApproved = false
): TCommunicationTask["status"] => {
  if (isManagerApproved) {
    return CommunicationConst.statuses.APPROVED;
  }

  const standNumbers = getNormalizedStandNumbers([
    ...(values.stand_numbers || []),
    values.stand_number,
  ]);

  return values.event_name?.trim() && values.company_name?.trim() && standNumbers.length > 0
    ? CommunicationConst.statuses.PENDING
    : CommunicationConst.statuses.UNIDENTIFIED;
};

const patchTaskWithFormValues = (
  task: TCommunicationTask,
  values: TCommunicationTaskFormValues,
  options?: {
    touchedAt?: string;
    assignee?: string;
    isApproved?: boolean;
  }
): TCommunicationTask => {
  const finalChanges = mapFormValuesToFinalChanges(values);
  const touchedAt = options?.touchedAt || new Date().toISOString();
  const isApproved = Boolean(options?.isApproved);
  const standNumbers = getNormalizedStandNumbers([
    ...(values.stand_numbers || []),
    values.stand_number,
  ]);
  const primaryStandNumber = standNumbers[0] || getString(values.stand_number);

  return {
    ...task,
    event_name: getString(values.event_name),
    company_name: getString(values.company_name),
    stand_number: primaryStandNumber,
    stand_numbers: standNumbers,
    organizer_id: values.organizer_id || undefined,
    exhibition_type_id: values.exhibition_type_id || undefined,
    questions: values.questions || [],
    final_changes: finalChanges,
    status: getTaskStatusFromValues(values, isApproved),
    updated_at: touchedAt,
    work_state: {
      ...task.work_state,
      status: isApproved ? "approved" : "in_progress",
      is_placeholder: true,
      assignee: options?.assignee || task.work_state.assignee || undefined,
      touched_at: touchedAt,
    },
  };
};

const getPrimaryRequest = (requests: TRawEmailRequest[]): TRawEmailRequest | undefined =>
  [ ...requests ].sort((left, right) => {
    const leftCompleteness = [
      pickFirstNonEmpty(left.exhibition_name, left.relExhibitionEvent?.title),
      pickFirstNonEmpty(left.company_name, left.relStand?.company_name),
      pickFirstNonEmpty(left.stand_number, left.relStand?.stand_number),
    ].filter(Boolean).length;
    const rightCompleteness = [
      pickFirstNonEmpty(right.exhibition_name, right.relExhibitionEvent?.title),
      pickFirstNonEmpty(right.company_name, right.relStand?.company_name),
      pickFirstNonEmpty(right.stand_number, right.relStand?.stand_number),
    ].filter(Boolean).length;

    if (rightCompleteness !== leftCompleteness) {
      return rightCompleteness - leftCompleteness;
    }

    return normalizeConfidence(right.confidence) - normalizeConfidence(left.confidence);
  })[ 0 ];

const getPrimaryFlooringRequest = (requests: TRawEmailRequest[]): TRawEmailRequest | undefined =>
  requests.find((request) => getString(request.flooring_id) || getString(request.relFlooring?.id));

const mapQuestionStatus = (
  status: TRawEmailRequest["status"]
): TCommunicationQuestion["status"] => (status === "APPROVED" ? "reviewed" : "open");

const mapRequestStatus = (
  status: TRawEmailRequest["status"]
): TCommunicationRequestItem["status"] => {
  if (status === "APPROVED") {
    return "approved";
  }

  if (status === "REJECTED") {
    return "rejected";
  }

  if (status === "CANCELLED") {
    return "cancelled";
  }

  if (status === "UNIDENTIFIED") {
    return "unidentified";
  }

  return "pending";
};

const toLowercaseAction = (
  action: TRawEmailRequest["action"]
): TCommunicationEquipmentChange["action"] => {
  if (action === "REMOVE") {
    return "remove";
  }

  if (action === "CHANGE") {
    return "change";
  }

  return "add";
};

const mapRequestAction = (
  action: TRawEmailRequest["action"]
): TCommunicationRequestItem["action"] => {
  if (action === "QUESTION") {
    return "question";
  }

  return toLowercaseAction(action);
};

const buildTaskCapabilities = (): TCommunicationTaskCapabilities => ({
  open_in_mystand: {
    enabled: false,
    mode: "concept",
    note_key: "COMMUNICATION.Concept_OpenInMyStandNote",
    href: null,
  },
  reply_to_customer: {
    enabled: false,
    mode: "concept",
    note_key: "COMMUNICATION.Concept_ReplyNote",
    href: null,
  },
  work_state_persistence: {
    enabled: false,
    mode: "concept",
    note_key: "COMMUNICATION.Concept_WorkStateNote",
    href: null,
  },
});

const mapRequestToItem = (
  request: TRawEmailRequest,
  incoming: TRawEmailIncoming
): TCommunicationRequestItem => ({
  id: pickFirstNonEmpty(request.id, `request-${incoming.id}`),
  email_request_id: pickFirstNonEmpty(request.id, `request-${incoming.id}`),
  email_incoming_id: pickFirstNonEmpty(request.email_incoming_id, incoming.id),
  event_name: pickFirstNonEmpty(request.exhibition_name, request.relExhibitionEvent?.title),
  event_id: pickFirstNonEmpty(request.ee_id, request.relExhibitionEvent?.id) || undefined,
  company_name: pickFirstNonEmpty(request.company_name, request.relStand?.company_name),
  stand_number: pickFirstNonEmpty(request.stand_number, request.relStand?.stand_number),
  stand_id: pickFirstNonEmpty(request.stand_id, request.relStand?.id) || undefined,
  action: mapRequestAction(request.action),
  status: mapRequestStatus(request.status),
  item_name: pickFirstNonEmpty(request.item_name, request.relEquipment?.title, request.item_notes),
  item_qty: request.item_qty || 1,
  item_notes: pickFirstNonEmpty(request.item_notes, request.notes) || undefined,
  confidence: normalizeConfidence(request.confidence),
  equipment_title: pickFirstNonEmpty(request.relEquipment?.title) || undefined,
  equipment_id: pickFirstNonEmpty(request.equipment_id, request.relEquipment?.id) || undefined,
  equipment_group_id: pickFirstNonEmpty(request.relEquipment?.group_id) || undefined,
  flooring_id: pickFirstNonEmpty(request.flooring_id, request.relFlooring?.id) || undefined,
  processed_by_id: pickFirstNonEmpty(request.processed_by_id) || undefined,
  processed_at: pickFirstNonEmpty(request.processed_at) || undefined,
  created_at: pickFirstNonEmpty(request.created_at) || undefined,
  updated_at: pickFirstNonEmpty(request.updated_at) || undefined,
});

const buildRequestGroupKey = (request: TCommunicationRequestItem): string => {
  const normalizedStandId = getString(request.stand_id);

  if (normalizedStandId) {
    return `stand:${normalizedStandId}`;
  }

  const normalizedStandNumber = getString(request.stand_number) || "unassigned";
  const normalizedCompanyName = getString(request.company_name) || "unassigned";
  const normalizedEventName = getString(request.event_name) || "unassigned";

  return `group:${normalizedEventName}|${normalizedCompanyName}|${normalizedStandNumber}`;
};

const buildRequestGroups = (
  requestItems: TCommunicationRequestItem[]
): TCommunicationRequestGroup[] =>
  Array.from(
    requestItems
      .reduce<Map<string, TCommunicationRequestGroup>>((groupsMap, request) => {
        const groupKey = buildRequestGroupKey(request);
        const existingGroup = groupsMap.get(groupKey);

        if (existingGroup) {
          existingGroup.requests.push(request);
          existingGroup.change_count += request.action === "question" ? 0 : 1;
          existingGroup.question_count += request.action === "question" ? 1 : 0;
          existingGroup.unidentified_count += request.status === "unidentified" ? 1 : 0;
          return groupsMap;
        }

        groupsMap.set(groupKey, {
          id: groupKey,
          event_name: request.event_name,
          event_id: request.event_id,
          company_name: request.company_name,
          stand_number: request.stand_number,
          stand_id: request.stand_id,
          requests: [ request ],
          change_count: request.action === "question" ? 0 : 1,
          question_count: request.action === "question" ? 1 : 0,
          unidentified_count: request.status === "unidentified" ? 1 : 0,
        });

        return groupsMap;
      }, new Map<string, TCommunicationRequestGroup>())
      .values()
  )
    .map((group) => ({
      ...group,
      requests: [ ...group.requests ].sort((left, right) => {
        if (left.action !== right.action) {
          return left.action === "question" ? 1 : -1;
        }

        return right.confidence - left.confidence;
      }),
    }))
    .sort((left, right) => {
      const leftStand = getString(left.stand_number);
      const rightStand = getString(right.stand_number);

      if (leftStand && rightStand && leftStand !== rightStand) {
        return leftStand.localeCompare(rightStand);
      }

      if (leftStand || rightStand) {
        return leftStand ? -1 : 1;
      }

      const leftCompany = getString(left.company_name);
      const rightCompany = getString(right.company_name);

      return `${leftCompany} ${left.event_name}`.localeCompare(`${rightCompany} ${right.event_name}`);
    });

const buildWorkState = (
  requests: TRawEmailRequest[],
  incoming: TRawEmailIncoming
): TCommunicationTaskWorkState => {
  const touchedAt = getLatestDate(
    requests.map((request) => pickFirstNonEmpty(request.processed_at, request.updated_at))
  );
  const hasProcessedRequest = requests.some(
    (request) => Boolean(getString(request.processed_at) || getString(request.processed_by_id))
  );
  const hasApprovedRequest = requests.length > 0
    && requests.every((request) => mapRequestStatus(request.status) === "approved");

  return {
    status: hasApprovedRequest
      ? "approved"
      : hasProcessedRequest
        ? "in_progress"
        : "untouched",
    is_placeholder: true,
    touched_at: touchedAt || pickFirstNonEmpty(incoming.parsed_at) || undefined,
    assignee: undefined,
  };
};

const createDemoMultiStandTask = (): TCommunicationTask => {
  const receivedAt = "2026-03-26T18:45:00";
  const primaryStandNumber = "OG-114";
  const secondaryStandNumber = "OG-127";
  const requestItems: TCommunicationRequestItem[] = [
    {
      id: "demo-request-1",
      email_request_id: "demo-request-1",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: primaryStandNumber,
      stand_id: "demo-stand-og-114",
      action: "add",
      status: "pending",
      item_name: "Textile wall graphic",
      item_qty: 10,
      item_notes: `Front wall print for stand ${primaryStandNumber}`,
      confidence: 86,
      equipment_title: "Textile wall graphic",
      equipment_group_id: "demo-group-graphic",
      created_at: receivedAt,
      updated_at: receivedAt,
    },
    {
      id: "demo-request-2",
      email_request_id: "demo-request-2",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: primaryStandNumber,
      stand_id: "demo-stand-og-114",
      action: "remove",
      status: "unidentified",
      item_name: "Halo panel",
      item_qty: 2,
      item_notes: "Please match this item to the catalog or delete it",
      confidence: 58,
      created_at: receivedAt,
      updated_at: receivedAt,
    },
    {
      id: "demo-request-3",
      email_request_id: "demo-request-3",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: secondaryStandNumber,
      stand_id: "demo-stand-og-127",
      action: "add",
      status: "pending",
      item_name: "Counter graphic panel",
      item_qty: 1,
      item_notes: `New counter branding for stand ${secondaryStandNumber}`,
      confidence: 74,
      equipment_title: "Counter graphic panel",
      equipment_group_id: "demo-group-counter",
      created_at: receivedAt,
      updated_at: receivedAt,
    },
    {
      id: "demo-request-4",
      email_request_id: "demo-request-4",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: secondaryStandNumber,
      stand_id: "demo-stand-og-127",
      action: "question",
      status: "pending",
      item_name: "Question about logo wall position",
      item_qty: 1,
      item_notes: `Can the logo wall for stand ${secondaryStandNumber} be moved to the back corner?`,
      confidence: 63,
      created_at: receivedAt,
      updated_at: receivedAt,
    },
  ];

  const requestGroups = buildRequestGroups(requestItems);
  const questions: TCommunicationQuestion[] = [
    {
      id: "demo-question-1",
      text: `Can the logo wall for stand ${secondaryStandNumber} be moved to the back corner?`,
      status: "open",
    },
  ];
  const equipmentChanges: TCommunicationEquipmentChange[] = [
    {
      id: "demo-change-1",
      action: "add",
      item_label: "Textile wall graphic",
      stand_number: primaryStandNumber,
      equipment_title: "Textile wall graphic",
      qty: 10,
      color: "Black",
      equipment_group_id: "demo-group-graphic",
      suggested_equipment_group_id: "demo-group-graphic",
      suggestion_status: "pending",
    },
    {
      id: "demo-change-2",
      action: "remove",
      item_label: "Halo panel",
      stand_number: primaryStandNumber,
      qty: 2,
      color: null,
    },
    {
      id: "demo-change-3",
      action: "add",
      item_label: "Counter graphic panel",
      stand_number: secondaryStandNumber,
      equipment_title: "Counter graphic panel",
      qty: 1,
      color: "Black",
      equipment_group_id: "demo-group-counter",
      suggested_equipment_group_id: "demo-group-counter",
      suggestion_status: "pending",
    },
  ];
  const finalChanges = createFinalChanges(equipmentChanges, {}, "");

  return {
    id: DEMO_MULTI_STAND_TASK_ID,
    source_email_id: DEMO_MULTI_STAND_TASK_ID,
    sender: "AAA Messen <aaa@messeprojekt.de>",
    sender_email: "aaa@messeprojekt.de",
    subject: `DEMO: AAA Wels 2026 multi-stand equipment request (${primaryStandNumber} + ${secondaryStandNumber})`,
    event_name: "AAA Wels 2026",
    stand_number: primaryStandNumber,
    stand_numbers: [ primaryStandNumber, secondaryStandNumber ],
    stand_id: "demo-stand-og-114",
    company_name: "Endress+Hauser Österreich",
    status: CommunicationConst.statuses.PENDING,
    confidence: 86,
    original_body:
      "Demo multi-stand case for UI validation.\n\n"
      + `Please add 10 textile wall graphics for stand ${primaryStandNumber}, remove 2 halo panels from stand ${primaryStandNumber}, `
      + `and add 1 counter graphic panel for stand ${secondaryStandNumber}. Also, can the logo wall for stand ${secondaryStandNumber} be moved to the back corner?`,
    ai_summary:
      "Demonstration email with parsed requests for two different stands of the same company.",
    ai_reason:
      "This synthetic example is kept in the board to validate the multi-stand email workflow before real backend cases arrive.",
    questions,
    request_items: requestItems,
    request_groups: requestGroups,
    work_state: {
      status: "untouched",
      is_placeholder: true,
      touched_at: receivedAt,
      assignee: undefined,
    },
    capabilities: buildTaskCapabilities(),
    extracted_changes: finalChanges,
    final_changes: finalChanges,
    received_at: receivedAt,
    parser_processed_at: receivedAt,
    approved_at: undefined,
    approved_by: undefined,
    updated_at: receivedAt,
    demo_badge_key: "COMMUNICATION.Demo_MultiStandBadge",
    demo_note_key: "COMMUNICATION.Demo_MultiStandNote",
  };
};

const extractAiSummary = (incoming: TRawEmailIncoming, requests: TRawEmailRequest[]): string =>
  readNestedString(incoming.parsed_data, AI_SUMMARY_KEYS)
  || requests.map((request) => readNestedString(request.ai_data, AI_SUMMARY_KEYS)).find(Boolean)
  || "";

const extractAiReason = (incoming: TRawEmailIncoming, requests: TRawEmailRequest[]): string =>
  getString(incoming.parse_error)
  || readNestedString(incoming.parsed_data, AI_REASON_KEYS)
  || requests.map((request) => readNestedString(request.ai_data, AI_REASON_KEYS)).find(Boolean)
  || "";

const mapIncomingToTask = (incoming: TRawEmailIncoming): TCommunicationTask => {
  const requests = incoming.relEmailRequests || [];
  const requestItems = requests.map((request) => mapRequestToItem(request, incoming));
  const requestGroups = buildRequestGroups(requestItems);
  const standNumbers = getNormalizedStandNumbers(
    requestGroups.map((group) => group.stand_number)
  );
  const primaryRequest = getPrimaryRequest(requests);
  const flooringRequest = getPrimaryFlooringRequest(requests);
  const eventName = pickFirstNonEmpty(
    primaryRequest?.exhibition_name,
    primaryRequest?.relExhibitionEvent?.title
  );
  const companyName = pickFirstNonEmpty(
    primaryRequest?.company_name,
    primaryRequest?.relStand?.company_name
  );
  const standNumber = pickFirstNonEmpty(
    primaryRequest?.stand_number,
    primaryRequest?.relStand?.stand_number
  );
  const questions = requests
    .filter((request) => request.action === "QUESTION")
    .map((request) => ({
      id: pickFirstNonEmpty(request.id, `question-${incoming.id}`),
      text: pickFirstNonEmpty(request.item_notes, request.item_name, request.notes),
      status: mapQuestionStatus(request.status),
    }))
    .filter((question) => question.text);
  const equipmentChanges = requests
    .filter((request) => request.action && request.action !== "QUESTION")
    .map((request): TCommunicationEquipmentChange => {
      const suggestedEquipmentGroupId = pickFirstNonEmpty(request.relEquipment?.group_id) || undefined;

      return {
        id: pickFirstNonEmpty(request.id, `change-${incoming.id}`),
        action: toLowercaseAction(request.action),
        item_label: pickFirstNonEmpty(request.item_name, request.relEquipment?.title),
        stand_number: pickFirstNonEmpty(request.stand_number, request.relStand?.stand_number) || undefined,
        equipment_title: pickFirstNonEmpty(request.relEquipment?.title) || undefined,
        qty: request.item_qty || 1,
        color: pickFirstNonEmpty(request.relEquipment?.color, request.relFlooring?.color) || null,
        equipment_id: pickFirstNonEmpty(request.equipment_id, request.relEquipment?.id) || undefined,
        equipment_group_id: suggestedEquipmentGroupId,
        suggested_equipment_group_id: suggestedEquipmentGroupId,
        suggestion_status: suggestedEquipmentGroupId ? "pending" : undefined,
      };
    })
    .filter((change) => change.item_label);
  const standUpdates: TCommunicationStandUpdates = {
    flooring_id: pickFirstNonEmpty(
      flooringRequest?.flooring_id,
      flooringRequest?.relFlooring?.id
    ) || undefined,
    flooring_color: pickFirstNonEmpty(flooringRequest?.relFlooring?.color) || undefined,
  };
  const managerNote = uniqueValues(
    requests.map((request) => getString(request.notes)).filter(Boolean)
  ).join("\n\n");
  const finalChanges = createFinalChanges(equipmentChanges, standUpdates, managerNote);
  const hasRequiredMatch = Boolean(eventName && companyName && standNumber);
  const isFullyApproved = requestItems.length > 0
    && requestItems.every((request) => request.status === "approved");
  const parserProcessedAt =
    getLatestDate([
      incoming.parsed_at,
      ...requests.map((request) => pickFirstNonEmpty(request.processed_at)),
    ]);

  return {
    id: pickFirstNonEmpty(incoming.id),
    source_email_id: pickFirstNonEmpty(incoming.id),
    sender: pickFirstNonEmpty(incoming.from_name)
      ? `${pickFirstNonEmpty(incoming.from_name)} <${pickFirstNonEmpty(incoming.from_email)}>`
      : pickFirstNonEmpty(incoming.from_email),
    sender_email: pickFirstNonEmpty(incoming.from_email) || undefined,
    subject: pickFirstNonEmpty(incoming.subject),
    event_name: eventName,
    event_id: pickFirstNonEmpty(primaryRequest?.ee_id) || undefined,
    stand_number: standNumber,
    stand_numbers: standNumbers.length ? standNumbers : getNormalizedStandNumbers([ standNumber ]),
    stand_id: pickFirstNonEmpty(primaryRequest?.stand_id) || undefined,
    company_name: companyName,
    status:
      isFullyApproved
        ? CommunicationConst.statuses.APPROVED
        : !requests.length || !hasRequiredMatch
          ? CommunicationConst.statuses.UNIDENTIFIED
          : CommunicationConst.statuses.PENDING,
    confidence: Math.max(0, ...requests.map((request) => normalizeConfidence(request.confidence))),
    original_body: pickFirstNonEmpty(incoming.body_clean),
    ai_summary: extractAiSummary(incoming, requests),
    ai_reason: extractAiReason(incoming, requests),
    questions,
    request_items: requestItems,
    request_groups: requestGroups,
    work_state: buildWorkState(requests, incoming),
    capabilities: buildTaskCapabilities(),
    organizer_id: pickFirstNonEmpty(primaryRequest?.relExhibitionEvent?.organizer_id) || undefined,
    exhibition_type_id:
      pickFirstNonEmpty(primaryRequest?.relExhibitionEvent?.exhibition_type_id) || undefined,
    extracted_changes: finalChanges,
    final_changes: finalChanges,
    received_at: pickFirstNonEmpty(incoming.received_at, incoming.created_at),
    parser_processed_at: parserProcessedAt,
    approved_at: undefined,
    approved_by: undefined,
    updated_at:
      getLatestDate([
        incoming.updated_at,
        incoming.parsed_at,
        incoming.received_at,
        ...requests.map((request) =>
          pickFirstNonEmpty(request.processed_at, request.updated_at, request.created_at)
        ),
      ]) || pickFirstNonEmpty(incoming.received_at),
    demo_badge_key: undefined,
    demo_note_key: undefined,
  };
};

const mapTasksToAuditEntries = (tasks: TCommunicationTask[]): TCommunicationAuditEntry[] =>
  tasks
    .filter((task) =>
      task.status === CommunicationConst.statuses.APPROVED
      || task.status === CommunicationConst.statuses.DISMISSED
    )
    .map((task) => ({
      id: `audit-${task.id}`,
      task_id: task.id,
      source_email_id: task.source_email_id,
      outcome:
        task.status === CommunicationConst.statuses.DISMISSED
          ? "dismissed"
          : "approved",
      before_changes: task.extracted_changes,
      after_changes: task.final_changes,
      before_questions: task.questions,
      after_questions: task.questions,
      processed_at:
        task.dismissed_at
        || task.approved_at
        || task.updated_at
        || task.received_at,
      processed_by: task.dismissed_by || task.approved_by || "",
      dismissed_reason: task.dismissed_reason || undefined,
      email_snapshot: {
        sender: task.sender,
        subject: task.subject,
        original_body: task.original_body,
        event_name: task.event_name,
        company_name: task.company_name,
        stand_number: task.stand_number,
      },
    }));

const sortTasksForBoard = (
  left: TCommunicationTask,
  right: TCommunicationTask
): number => {
  const leftIsDemo = Boolean(left.demo_badge_key);
  const rightIsDemo = Boolean(right.demo_badge_key);

  if (leftIsDemo !== rightIsDemo) {
    return leftIsDemo ? -1 : 1;
  }

  return new Date(right.received_at).getTime() - new Date(left.received_at).getTime();
};

export const getCommunicationSummary = (tasks: TCommunicationTask[]): TCommunicationSummary => {
  const todayKey = getCommunicationDayKey();
  const queueTotals: TCommunicationQueueTotals = {
    inbox: tasks.filter((task) =>
      task.status !== CommunicationConst.statuses.APPROVED
      && task.status !== CommunicationConst.statuses.DISMISSED
    ).length,
    pending: tasks.filter((task) => task.status === CommunicationConst.statuses.PENDING).length,
    unidentified: tasks.filter((task) => task.status === CommunicationConst.statuses.UNIDENTIFIED)
      .length,
    parserProcessedToday: tasks.filter(
      (task) =>
        Boolean(task.parser_processed_at)
        && getCommunicationDayKey(task.parser_processed_at) === todayKey
    ).length,
    managerApprovedToday: tasks.filter(
      (task) =>
        Boolean(task.approved_at)
        && getCommunicationDayKey(task.approved_at) === todayKey
    ).length,
  };

  return { queueTotals };
};

const fetchPaginatedData = async <TItem,>(
  client: ApolloClient<object>,
  query: any,
  model: string,
  pageSize: number,
  baseVariables: Record<string, unknown>,
  isStale: () => boolean
): Promise<TItem[] | undefined> => {
  const aggregatedItems: TItem[] = [];
  let currentPage = 1;
  let lastPage = 1;

  do {
    if (isStale()) {
      return undefined;
    }

    const response = await client.query<{ [ key: string ]: TPaginatorData<TItem> }>({
      query,
      variables: {
        ...baseVariables,
        first: pageSize,
        page: currentPage,
      },
      fetchPolicy: "network-only",
    });

    const payload = response.data?.[ model ];
    aggregatedItems.push(...(payload?.data || []));
    lastPage = payload?.paginatorInfo?.lastPage || currentPage;
    currentPage += 1;
  } while (currentPage <= lastPage);

  return aggregatedItems;
};

export const useCommunicationStore = (
  filters: TCommunicationFilters,
  currentManagerName?: string
) => {
  const client = useApolloClient();
  const requestIdRef = useRef(0);
  const [ tasks, setTasks ] = useState<TCommunicationTask[]>([]);
  const [ loading, setLoading ] = useState<boolean>(true);
  const [ error, setError ] = useState<string>();

  const loadTasks = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    setLoading(true);
    setError(undefined);

    try {
      const requestWhere = buildRequestWhere(filters);
      const incomingWhere = buildIncomingWhere(filters);
      let incomings: TRawEmailIncoming[] = [];

      if (hasRequestLevelFilters(filters) && requestWhere) {
        const matchedRequests = await fetchPaginatedData<TRawEmailRequest>(
          client,
          GET_EMAIL_REQUESTS,
          "emailRequests",
          REQUEST_PAGE_SIZE,
          {
            where: requestWhere,
            orderBy: [ { column: "ID", order: "DESC" } ],
          },
          isStale
        );

        if (isStale()) {
          return;
        }

        const incomingIds = uniqueValues(
          (matchedRequests || []).map((request) => getString(request.email_incoming_id))
        );

        if (!incomingIds.length) {
          setTasks([]);
          setLoading(false);
          return;
        }

        const incomingIdChunks = chunkArray(incomingIds, INCOMING_ID_CHUNK_SIZE);
        const incomingResults: TRawEmailIncoming[] = [];

        for (const incomingIdChunk of incomingIdChunks) {
          if (isStale()) {
            return;
          }

          const response = await client.query<{ emailIncomings: TPaginatorData<TRawEmailIncoming> }>({
            query: GET_EMAIL_INCOMINGS,
            variables: {
              first: incomingIdChunk.length,
              page: 1,
              where: buildIncomingWhere(filters, incomingIdChunk),
              orderBy: [ { column: "RECEIVED_AT", order: "DESC" } ],
            },
            fetchPolicy: "network-only",
          });

          incomingResults.push(...(response.data?.emailIncomings?.data || []));
        }

        incomings = incomingResults;
      } else {
        incomings = (
          await fetchPaginatedData<TRawEmailIncoming>(
            client,
            GET_EMAIL_INCOMINGS,
            "emailIncomings",
            INCOMING_PAGE_SIZE,
            {
              where: incomingWhere,
              orderBy: [ { column: "RECEIVED_AT", order: "DESC" } ],
            },
            isStale
          )
        ) || [];
      }

      if (isStale()) {
        return;
      }

      const loadedTasks = Array.from(
        incomings
          .reduce<Map<string, TCommunicationTask>>((acc, incoming) => {
            const incomingId = getString(incoming.id);

            if (!incomingId) {
              return acc;
            }

            acc.set(incomingId, mapIncomingToTask(incoming));
            return acc;
          }, new Map<string, TCommunicationTask>())
          .values()
      );
      const demoTask = createDemoMultiStandTask();
      const nextTasks = [ demoTask, ...loadedTasks.filter((task) => task.id !== demoTask.id) ]
        .filter((task) => matchesClientSideFilters(task, filters))
        .sort(sortTasksForBoard);

      setTasks(nextTasks);
      setLoading(false);
    } catch (nextError) {
      if (isStale()) {
        return;
      }

      setTasks([]);
      setError(nextError instanceof Error ? nextError.message : "Failed to load communications");
      setLoading(false);
    }
  }, [
    client,
    filters.company_name,
    filters.event_name,
    filters.received_date,
    filters.sender,
    filters.stand_number,
  ]);

  useEffect(() => {
    loadTasks();

    return () => {
      requestIdRef.current += 1;
    };
  }, [ loadTasks ]);

  const auditEntries = useMemo(() => mapTasksToAuditEntries(tasks), [ tasks ]);
  const summary = useMemo(() => getCommunicationSummary(tasks), [ tasks ]);
  const saveTask = useCallback((taskId: string, values: TCommunicationTaskFormValues) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId
          ? patchTaskWithFormValues(task, values, {
            touchedAt: new Date().toISOString(),
            assignee: currentManagerName,
          })
          : task
      )
    );
  }, [ currentManagerName ]);
  const approveTask = useCallback(
    (taskId: string, values: TCommunicationTaskFormValues) => {
      const approvedAt = new Date().toISOString();
      const approvedBy = getString(currentManagerName) || "Current manager";

      setTasks((currentTasks) =>
        currentTasks.map((task) => {
          if (task.id !== taskId) {
            return task;
          }

          const nextTask = patchTaskWithFormValues(task, values, {
            touchedAt: approvedAt,
            assignee: approvedBy,
            isApproved: true,
          });

          return {
            ...nextTask,
            status: getTaskStatusFromValues(values, true),
            approved_at: approvedAt,
            approved_by: approvedBy,
            updated_at: approvedAt,
            work_state: {
              ...nextTask.work_state,
              status: "approved",
              is_placeholder: true,
              assignee: approvedBy,
              touched_at: approvedAt,
            },
          };
        })
      );
    },
    [ currentManagerName ]
  );

  const markAsInProgress = useCallback((taskId: string) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId && task.work_state.status === "untouched"
          ? {
            ...task,
            work_state: {
              ...task.work_state,
              status: "in_progress" as const,
              is_placeholder: true,
              assignee: currentManagerName,
              touched_at: new Date().toISOString(),
            },
          }
          : task
      )
    );
  }, [ currentManagerName ]);

  const dismissTask = useCallback((taskId: string, reason: TCommunicationDismissReason) => {
    const dismissedAt = new Date().toISOString();
    const dismissedBy = getString(currentManagerName) || "Current manager";

    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId
          ? {
            ...task,
            status: CommunicationConst.statuses.DISMISSED,
            dismissed_at: dismissedAt,
            dismissed_by: dismissedBy,
            dismissed_reason: reason,
            updated_at: dismissedAt,
            work_state: {
              ...task.work_state,
              status: "approved",
              is_placeholder: true,
              assignee: dismissedBy,
              touched_at: dismissedAt,
            },
          }
          : task
      )
    );
  }, [ currentManagerName ]);

  return {
    tasks,
    auditEntries,
    saveTask,
    approveTask,
    dismissTask,
    markAsInProgress,
    summary,
    loading,
    error,
  };
};
